import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, deleteDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_APIKEY,

  authDomain: process.env.REACT_APP_AUTHDOMAIN,

  projectId: process.env.REACT_APP_PROYECTID,

  storageBucket: process.env.REACT_APP_STORAGEBUCKET,

  messagingSenderId: process.env.REACT_APP_MESSAGINGSENDERID,

  appId: process.env.REACT_APP_APPID,

  measurementId: process.env.REACT_APP_MEASUREMENTID,
};
export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// Analytics solo funciona en el navegador. Se inicializa de forma asíncrona
// con isSupported() para evitar errores en entornos sin soporte
// (renderizado en servidor, navegadores viejos, bloqueadores de tracking).
export let analytics: Analytics | null = null;
export const analyticsReady: Promise<Analytics | null> = isSupported()
  .then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      return analytics;
    }
    return null;
  })
  .catch((error) => {
    console.log("Analytics no disponible:", error);
    return null;
  });

export interface FirestoreTimestampLike {
  seconds: number;
  nanoseconds?: number;
}

export interface UserSkillRecord {
  Materia: string;
  Progress: number;
}

export interface UserAchievementRecord {
  Name: string;
  Description: string;
  Status: string;
  Icon?: unknown;
}

export interface UserNotificationRecord {
  Title: string;
  Description: string;
  Checked: boolean;
  Icon?: unknown;
  Date?: FirestoreTimestampLike | null;
}

//USUARIOS
//Create a template para los Usuarios
export interface Task {
  id: string;
  Nombre: string;
  Email: string;
  Rol: string;
  Nivel: number;
  CurrentExp: number;
  XpAcademica?: number;
  Skill: UserSkillRecord[];
  Materias: unknown[];
  PortraitImg: string;
  Puntos: number;
  Recompensas: unknown[];
  Logros: UserAchievementRecord[];
  Items: unknown[];
  Notifications: UserNotificationRecord[];
  Date?: FirestoreTimestampLike | Date | null;
}

export interface NewsTask {
  id: string;
  Titulo: string;
  Subtitle: string;
  Cuerpo: string;
  Imagen: string;
  Video: string;        // link de YouTube (opcional, "" si no tiene)
  Exp: number;          // XP que se puede ganar (0 = no suma)
  ExpDetalle: string;   // qué hay que hacer para ganarla (opcional)
  Inscripcion: boolean; // muestra el botón "Inscribirme" (11/10/2026)
  FechaFin: string;     // ISO; desde ese momento deja de mostrarse ("" = no vence)
}

// ══════════════════════════════════════════════════════════════
// FECHA DE FINALIZACIÓN DE NOTICIAS (agregado 11/10/2026)
//
// Campo opcional Noticias/{id}.FechaFin (ISO string, igual que la
// fecha de los Eventos). Cuando pasa esa fecha y hora, la noticia
// deja de aparecer en el carrusel de los usuarios. En Admin sigue
// visible con la etiqueta "Finalizada" (y su lista de inscriptos).
// Vacío = la noticia no vence nunca, como hasta ahora.
// ══════════════════════════════════════════════════════════════
export const isNewsFinished = (fechaFin?: string, now: number = Date.now()) => {
  if (!fechaFin) return false;
  const end = new Date(fechaFin).getTime();
  return Number.isFinite(end) && end <= now;
};

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asArray = <T = unknown>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const normalizeSkillRecord = (value: unknown): UserSkillRecord => ({
  Materia: asString((value as { Materia?: unknown })?.Materia, "Sin materia"),
  Progress: asNumber((value as { Progress?: unknown })?.Progress),
});

const normalizeAchievementRecord = (value: unknown): UserAchievementRecord => ({
  Name: asString((value as { Name?: unknown })?.Name, "Logro"),
  Description: asString((value as { Description?: unknown })?.Description),
  Status: asString((value as { Status?: unknown })?.Status, "Pendiente"),
  Icon: (value as { Icon?: unknown })?.Icon,
});

const normalizeNotificationRecord = (value: unknown): UserNotificationRecord => ({
  Title: asString((value as { Title?: unknown })?.Title, "Notificación"),
  Description: asString((value as { Description?: unknown })?.Description),
  Checked: Boolean((value as { Checked?: unknown })?.Checked),
  Icon: (value as { Icon?: unknown })?.Icon,
  Date: ((value as { Date?: FirestoreTimestampLike | null })?.Date ?? null),
});

export const normalizeUserRecord = (id: string, value: unknown): Task => ({
  id,
  Nombre: asString((value as { Nombre?: unknown })?.Nombre, "Usuario"),
  Email: asString(
    (value as { Email?: unknown; email?: unknown })?.Email ??
    (value as { email?: unknown })?.email,
  ),
  Rol: asString(
    (value as { Rol?: unknown; rol?: unknown })?.Rol ??
    (value as { rol?: unknown })?.rol,
    "regular",
  ),
  Nivel: asNumber(
    (value as { Nivel?: unknown; nivel?: unknown })?.Nivel ??
    (value as { nivel?: unknown })?.nivel,
  ),
  CurrentExp: asNumber(
    (value as { CurrentExp?: unknown; currentExp?: unknown })?.CurrentExp ??
    (value as { currentExp?: unknown })?.currentExp,
  ),
  XpAcademica:
    typeof (value as { XpAcademica?: unknown })?.XpAcademica === "number"
      ? ((value as { XpAcademica: number }).XpAcademica)
      : undefined,
  Skill: asArray((value as { Skill?: unknown })?.Skill).map(normalizeSkillRecord),
  Materias: asArray((value as { Materias?: unknown; materias?: unknown })?.Materias ?? (value as { materias?: unknown })?.materias),
  PortraitImg: asString(
    (value as { PortraitImg?: unknown; portraitimg?: unknown })?.PortraitImg ??
    (value as { portraitimg?: unknown })?.portraitimg,
  ),
  Puntos: asNumber(
    (value as { Puntos?: unknown; puntos?: unknown })?.Puntos ??
    (value as { puntos?: unknown })?.puntos,
  ),
  Recompensas: asArray(
    (value as { Recompensas?: unknown; recompensas?: unknown })?.Recompensas ??
    (value as { recompensas?: unknown })?.recompensas,
  ),
  Logros: asArray((value as { Logros?: unknown; logros?: unknown })?.Logros ?? (value as { logros?: unknown })?.logros).map(
    normalizeAchievementRecord,
  ),
  Items: asArray(
    (value as { Items?: unknown; items?: unknown })?.Items ??
    (value as { items?: unknown })?.items,
  ),
  Notifications: asArray((value as { Notifications?: unknown })?.Notifications).map(
    normalizeNotificationRecord,
  ),
  Date:
    ((value as { Date?: FirestoreTimestampLike | Date | null })?.Date ?? null),
});

const normalizeNewsRecord = (id: string, value: unknown): NewsTask => ({
  id,
  Titulo: asString((value as { Titulo?: unknown; Title?: unknown })?.Titulo ?? (value as { Title?: unknown })?.Title, "Sin título"),
  Subtitle: asString(
    (value as { Subtitle?: unknown; subtitle?: unknown; Subtitulo?: unknown })?.Subtitle ??
    (value as { subtitle?: unknown; Subtitulo?: unknown })?.subtitle ??
    (value as { Subtitulo?: unknown })?.Subtitulo,
  ),
  Cuerpo: asString((value as { Cuerpo?: unknown; Description?: unknown })?.Cuerpo ?? (value as { Description?: unknown })?.Description),
  Imagen: asString((value as { Imagen?: unknown; Image?: unknown })?.Imagen ?? (value as { Image?: unknown })?.Image),
  Video: asString(
    (value as { Video?: unknown; YoutubeUrl?: unknown })?.Video ??
    (value as { YoutubeUrl?: unknown })?.YoutubeUrl,
  ),
  Exp: Math.max(0, Math.round(asNumber((value as { Exp?: unknown })?.Exp))),
  ExpDetalle: asString((value as { ExpDetalle?: unknown })?.ExpDetalle),
  Inscripcion: (value as { Inscripcion?: unknown })?.Inscripcion === true,
  FechaFin: asString((value as { FechaFin?: unknown })?.FechaFin),
});

//Crear nueva entry en FireStore
export async function CreateNewEntry(nombre: string, email: string, nivel: number, currentExp: number, id: string) {
  try {
    await setDoc(doc(db, "Usuarios", id), {
      Nombre: nombre,
      Email: email,
      Rol: "regular",
      Nivel: nivel,
      CurrentExp: currentExp,
      Skill: "",
      Materias: [],
      PortraitImg: "",
      Puntos: 0,
      Recompensas: [],
      Logros: [],
      Items: [],
      Notifications: [],
      Date: new Date(),
    })
  } catch (error) {
    console.log(error);
  };
}

//Actualizar una entry en Firestore
export async function UpdateEntry(id: string, nombre: string, email: string, rol: string, nivel: number, currentExp: number, skill: string,
  materias: object, portraitimg: string, puntos: number, recompensas: string, logros: string, items: string) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        Nombre: nombre,
        Email: email,
        Rol: rol,
        Nivel: nivel,
        CurrentExp: currentExp,
        Skill: skill,
        Materias: materias,
        PortraitImg: portraitimg,
        Puntos: puntos,
        Recompensas: recompensas,
        Logros: logros,
        Items: items,
      }
    )
  } catch (error) {
    console.log(error)
  }
}

//Conseguir todas las Entries de la DB
export async function GetAllUsers() {
  try {
    const querySnapShot = await getDocs(collection(db, "Usuarios"));
    return querySnapShot.docs.map((entry) => normalizeUserRecord(entry.id, entry.data()))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Conseguir la informacion de UN solo usuario por ID
export const GetOneUser = async (id: string): Promise<Task | null> => {
  try {
    const querySnapShot = await getDoc(doc(db, "Usuarios", id));
    if (querySnapShot.exists()) {
      const userRecord = normalizeUserRecord(querySnapShot.id, querySnapShot.data());
      localStorage.setItem(id, JSON.stringify(userRecord))
      return userRecord;
    } else {
      return null
    }
  } catch (error) {
    console.log(error)
    throw error
  }
}

//Eliminar una entry en la DB
export async function DeleteUser(id: string) {
  try {
    await deleteDoc(doc(db, "Usuarios", id))
  } catch (error) {
    console.log(error)
    throw error
  }
}

//EVENTOS
export interface EventoTask {
  id: string;
  fecha: FirestoreTimestampLike | string;
  descripcion: string;
  imagen?: string;
  exp?: number;
  expDetalle?: string;
  inscripcion?: boolean; // muestra el botón "Inscribirme" (11/10/2026)
}

//Conseguir todos los Eventos de la DB
export async function GetAllEvents() {
  try {
    const querySnapShot = await getDocs(collection(db, "Eventos"));
    return querySnapShot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EventoTask))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Conseguir la informacion de UN solo Evento por ID
export const GetOneEvent = async (id: string): Promise<EventoTask | null> => {
  try {
    const querySnapShot = await getDoc(doc(db, "Eventos", id));
    if (querySnapShot.exists()) {
      return { id: querySnapShot.id, ...querySnapShot.data() } as EventoTask;
    } else {
      return null
    }
  } catch (error) {
    console.log(error)
    throw error
  }
}

//Eliminar una entry en la DB
export async function DeleteEvent(id: string) {
  try {
    await deleteDoc(doc(db, "Eventos", id))
  } catch (error) {
    console.log(error)
    throw error
  }
}

//Conseguir todos las Noticias de la DB
export async function GetAllNews() {
  try {
    const querySnapShot = await getDocs(collection(db, "Noticias"));
    return querySnapShot.docs.map((entry) => normalizeNewsRecord(entry.id, entry.data()))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Editado el 16/4
//Debido a la falta de tiempo y no ser capaz de decifrar el fucionamiento del nuevo codigo..
//realizo codigo rebundante como solucion temporal.

//Actualizar la PortraitImg de una entry
export async function UpdateEntryImg(id: string, portraitimg: string) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        PortraitImg: portraitimg,
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar las Recompensas de una entry
export async function UpdateEntryRewards(id: string, recompensas: unknown[]) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        Recompensas: recompensas
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar los Niveles de una entry
export async function UpdateEntryLevels(id: string, nivel: number) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        Nivel: nivel,
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar las Notificaciones de una entry
export async function UpdateEntryNotifications(id: string, notifications: UserNotificationRecord[]) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        Notifications: notifications,
      }
    )
  } catch (error) {
    console.log(error)
  }
}

// ══════════════════════════════════════════════════════════════
// PANEL DE ADMIN (agregado 26/08/2026)
//
// Funciones nuevas, separadas de las de arriba a propósito:
// - No tocan las funciones viejas (UpdateEntry, UpdateEntryRewards, etc.)
//   para no romper ninguna pantalla existente que ya las use.
// - Sí relanzan el error (throw) en vez de solo loguearlo, para que el
//   panel de Admin pueda mostrar un mensaje si falla un guardado. Es la
//   mejora que ya estaba anotada en claude/roadmap-mejoras.md.
// - CreateNewEvent/UpdateEvent usan los campos que realmente lee
//   EventCalendar.tsx (descripcion/fecha/imagen). La versión vieja de
//   CreateNewEvent escribía Nombre/Puntos/Imagen, que el calendario
//   nunca leyó: quedó reemplazada acá porque no la usaba ninguna
//   pantalla todavía.
// ══════════════════════════════════════════════════════════════

// ---------- USUARIOS: actualización parcial desde el panel de admin ----------
export type UserAdminUpdate = Partial<{
  Nombre: string;
  Rol: string;
  Nivel: number;
  CurrentExp: number;
  Puntos: number;
  PortraitImg: string;
  Skill: UserSkillRecord[];
  Recompensas: unknown[];
  Logros: UserAchievementRecord[];
  Notifications: UserNotificationRecord[];
  Items: unknown[];
}>;

export async function UpdateUserAdmin(id: string, updates: UserAdminUpdate) {
  try {
    await updateDoc(doc(db, "Usuarios", id), updates as any);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// ---------- NOTICIAS: crear / editar / eliminar ----------
export async function CreateNewsEntry(
  titulo: string,
  subtitle: string,
  cuerpo: string,
  imagen: string,
  video = "",
  exp = 0,
  expDetalle = "",
  inscripcion = false,
  fechaFin = "",
  id?: string,
) {
  try {
    const newsId = id || doc(collection(db, "Noticias")).id;
    await setDoc(doc(db, "Noticias", newsId), {
      Titulo: titulo,
      Subtitle: subtitle,
      Cuerpo: cuerpo,
      Imagen: imagen,
      Video: video,
      Exp: exp,
      ExpDetalle: expDetalle,
      Inscripcion: inscripcion,
      FechaFin: fechaFin,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function UpdateNewsEntry(
  id: string,
  updates: Partial<
    Pick<
      NewsTask,
      "Titulo" | "Subtitle" | "Cuerpo" | "Imagen" | "Video" | "Exp" | "ExpDetalle" | "Inscripcion" | "FechaFin"
    >
  >,
) {
  try {
    await updateDoc(doc(db, "Noticias", id), updates as any);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function DeleteNewsEntry(id: string) {
  try {
    await deleteDoc(doc(db, "Noticias", id));
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// ---------- EVENTOS: crear / editar (con los campos reales que usa EventCalendar) ----------
export async function CreateNewEvent(
  descripcion: string,
  fecha: string,
  imagen: string,
  exp = 0,
  expDetalle = "",
  inscripcion = false,
  id?: string,
) {
  try {
    const eventId = id || doc(collection(db, "Eventos")).id;
    await setDoc(doc(db, "Eventos", eventId), {
      descripcion,
      fecha,
      imagen,
      exp,
      expDetalle,
      inscripcion,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function UpdateEvent(
  id: string,
  updates: Partial<{
    descripcion: string;
    fecha: string;
    imagen: string;
    exp: number;
    expDetalle: string;
    inscripcion: boolean;
  }>,
) {
  try {
    await updateDoc(doc(db, "Eventos", id), updates as any);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════
// CATÁLOGO COMPARTIDO DE RECOMPENSAS (agregado 26/08/2026)
//
// Colección nueva "RecompensasCatalogo": la definición de cada
// recompensa (nombre, descripción, imagen, nivel, etc.) vive UNA
// sola vez acá, no copiada en cada usuario. El panel de Admin la
// edita como un catálogo normal (crear/editar/eliminar).
//
// Lo único que sigue viviendo en Usuarios/{uid}.Recompensas es un
// estado liviano por usuario: qué recompensas reclamó y cuál tiene
// equipada, referenciando el id del catálogo (RewardId). Ver
// mergeRewardsWithUserState() en utils/rewards.ts, que junta ambas
// cosas para el resto de la app.
//
// IMPORTANTE: esto requiere actualizar las reglas de Firestore
// (claude/firestore.rules.txt tiene el borrador con la regla nueva)
// y cambiar la creación de usuarios en Unity para que ya no escriba
// la lista vieja completa en Recompensas.
// ══════════════════════════════════════════════════════════════

export interface RewardCatalogEntry {
  id: string;
  Name: string;
  Description: string;
  Category: string;
  Icon: string;
  Rarity: string;
  Tone: string;
  Level: number;
  ExpRequired: number;
  Image: string;
  // ── Canje (agregado 01/09/2026) ──
  // Opcionales: si RedeemEnabled es false, la tarjeta no muestra el botón.
  RedeemEnabled: boolean;
  RedeemTitle: string;
  RedeemInstructions: string;
  RedeemImage: string;
}

const normalizeRewardCatalogRecord = (
  id: string,
  value: unknown,
): RewardCatalogEntry => ({
  id,
  Name: asString((value as { Name?: unknown })?.Name, "Recompensa"),
  Description: asString((value as { Description?: unknown })?.Description),
  Category: asString((value as { Category?: unknown })?.Category, "special"),
  Icon: asString((value as { Icon?: unknown })?.Icon),
  Rarity: asString((value as { Rarity?: unknown })?.Rarity, "common"),
  Tone: asString((value as { Tone?: unknown })?.Tone, "teal"),
  Level: asNumber((value as { Level?: unknown })?.Level, 1),
  ExpRequired: asNumber((value as { ExpRequired?: unknown })?.ExpRequired, 0),
  Image: asString((value as { Image?: unknown })?.Image),
  RedeemEnabled:
    (value as { RedeemEnabled?: unknown })?.RedeemEnabled === true,
  RedeemTitle: asString((value as { RedeemTitle?: unknown })?.RedeemTitle),
  RedeemInstructions: asString(
    (value as { RedeemInstructions?: unknown })?.RedeemInstructions,
  ),
  RedeemImage: asString((value as { RedeemImage?: unknown })?.RedeemImage),
});

//Conseguir todo el catálogo de recompensas (ordenado por nivel)
export async function GetRewardsCatalog(): Promise<RewardCatalogEntry[]> {
  try {
    const querySnapShot = await getDocs(collection(db, "RecompensasCatalogo"));
    return querySnapShot.docs
      .map((entry) => normalizeRewardCatalogRecord(entry.id, entry.data()))
      // Firestore devuelve los docs por ID, no por Level: ordenamos acá para
      // que el pase, el inventario y el panel de Admin muestren lo mismo.
      .sort((a, b) => a.Level - b.Level || a.Name.localeCompare(b.Name));
  } catch (error) {
    console.error(error);
    return [];
  }
}

export type RewardCatalogEntryInput = Omit<RewardCatalogEntry, "id">;

export async function CreateRewardCatalogEntry(
  data: RewardCatalogEntryInput,
  id?: string,
) {
  try {
    const rewardId = id || doc(collection(db, "RecompensasCatalogo")).id;
    await setDoc(doc(db, "RecompensasCatalogo", rewardId), data);
    return rewardId;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function UpdateRewardCatalogEntry(
  id: string,
  updates: Partial<RewardCatalogEntryInput>,
) {
  try {
    await updateDoc(doc(db, "RecompensasCatalogo", id), updates as any);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function DeleteRewardCatalogEntry(id: string) {
  try {
    await deleteDoc(doc(db, "RecompensasCatalogo", id));
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// ---------- Estado liviano de recompensas por usuario ----------
export interface UserRewardState {
  RewardId: string;
  Claimed: boolean;
  Equipped: boolean;
}

export async function UpdateUserRewardsState(
  id: string,
  state: UserRewardState[],
) {
  try {
    await updateDoc(doc(db, "Usuarios", id), { Recompensas: state } as any);
  } catch (error) {
    console.error(error);
    throw error;
  }
}
